import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  TextInput,
  Textarea,
  // Switch,
  Stack,
  Group,
  ActionIcon,
  Table,
  Container,
  LoadingOverlay,
  Text,
  Button
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconTrash, IconPlus } from '@tabler/icons-react';

import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import Card from 'components/Card';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
// import PageTitle from 'components/PageTitle';

/* eslint-disable */
import { rangeTemplateService, RangeTemplate } from 'services/lia/rangeTemplateService';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
/* eslint-enable */

const items = [
  { title: 'Range Templates', href: '/app/lia/questions/range-templates' },
  { title: 'Add Template', href: '' }
];

// Schema for range items
const rangeItemSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  value: z.string().min(1, 'Value is required'),
  sequence: z.number().optional()
});

// Main form schema
const templateSchema = z.object({
  templateName: z.string().min(1, 'Template name is required'),
  description: z.string().optional(),
  isDefault: z.boolean().default(false),
  items: z
    .array(rangeItemSchema)
    .min(2, 'At least 2 range items are required')
    .max(10, 'Maximum 10 range items allowed')
});

type FormData = z.infer<typeof templateSchema>;

const AddRangeTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient(); // Add this
  const [opened, { open, close }] = useDisclosure(false);
  const [formData, setFormData] = useState<FormData | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      templateName: '',
      description: '',
      isDefault: false,
      items: [
        { label: 'Poor', value: '1', sequence: 1 },
        { label: 'Fair', value: '2', sequence: 2 },
        { label: 'Good', value: '3', sequence: 3 }
      ]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const createMutation = useMutation({
    mutationFn: (data: FormData) => {
      const createDto = {
        templateName: data.templateName,
        description: data.description || '',
        isDefault: data.isDefault,
        items: data.items.map((item, index) => ({
          label: item.label,
          value: item.value,
          sequence: item.sequence || index + 1
        }))
      };

      return rangeTemplateService.create(createDto);
    },
    onSuccess: () => {
      // Invalidate the range templates query to force a refresh
      queryClient.invalidateQueries({ queryKey: ['rangeTemplates'] });

      navigate('/app/lia/questions/range-templates');
      showSuccessNotification({
        title: 'Created Successfully!',
        message: 'Range template has been created successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to create template',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const onSubmit = (data: FormData) => {
    setFormData(data);
    open();
  };

  const handleConfirmSubmit = () => {
    if (formData) {
      createMutation.mutate(formData);
    }
    close();
  };

  return (
    <Container fluid>
      <Stack gap='md'>
        <BreadcrumbsComponent items={items} />

        <Card>
          <div style={{ position: 'relative' }}>
            <LoadingOverlay visible={createMutation.isPending} />

            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap='md'>
                <Controller
                  name='templateName'
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      label='Template Name'
                      placeholder='e.g., 5-Point Likert Scale'
                      required
                      error={errors.templateName?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name='description'
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      label='Description'
                      placeholder='Describe what this template is used for'
                      {...field}
                    />
                  )}
                />

                {/* <Controller
                  name='isDefault'
                  control={control}
                  render={({ field: { value, onChange, ...rest } }) => (
                    <Switch
                      label='Set as default template'
                      description='This template will be suggested by default when creating range questions'
                      checked={value}
                      onChange={(event) => onChange(event.currentTarget.checked)}
                      {...rest}
                    />
                  )}
                /> */}

                <Text fw={500} mt='md'>
                  Range Items
                </Text>
                <Text size='sm' color='dimmed'>
                  Define the options that will be available in this template. The sequence
                  determines the order of display.
                </Text>

                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Label</Table.Th>
                      <Table.Th>Value</Table.Th>
                      <Table.Th w={100}>Sequence</Table.Th>
                      <Table.Th w={40}></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {fields.map((field, index) => (
                      <Table.Tr key={field.id}>
                        <Table.Td>
                          <Controller
                            name={`items.${index}.label`}
                            control={control}
                            render={({ field }) => (
                              <TextInput
                                placeholder='e.g., Strongly Agree'
                                error={errors.items?.[index]?.label?.message}
                                {...field}
                              />
                            )}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Controller
                            name={`items.${index}.value`}
                            control={control}
                            render={({ field }) => (
                              <TextInput
                                placeholder='e.g., 5'
                                error={errors.items?.[index]?.value?.message}
                                {...field}
                              />
                            )}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Controller
                            name={`items.${index}.sequence`}
                            control={control}
                            defaultValue={index + 1}
                            render={({ field: { onChange, value, ...rest } }) => (
                              <TextInput
                                type='number'
                                min={1}
                                value={value?.toString() || (index + 1).toString()}
                                onChange={(e) => onChange(parseInt(e.target.value) || index + 1)}
                                {...rest}
                              />
                            )}
                          />
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon
                            color='red'
                            variant='subtle'
                            onClick={() => remove(index)}
                            disabled={fields.length <= 2}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>

                <Button
                  leftSection={<IconPlus size={16} />}
                  variant='light'
                  color='#7B2183'
                  onClick={() =>
                    append({
                      label: '',
                      value: '',
                      sequence: fields.length + 1
                    })
                  }
                >
                  Add Item
                </Button>

                {errors.items?.message && (
                  <Text color='red' size='sm'>
                    {errors.items.message}
                  </Text>
                )}

                <Group justify='flex-end' mt='xl'>
                  <SecondaryButton
                    text='Cancel'
                    onClick={() => navigate('/app/lia/questions/range-templates')}
                    mr={10}
                  />
                  <PrimaryButton text='Save Template' type='submit' />
                </Group>
              </Stack>
            </form>
          </div>
        </Card>
      </Stack>

      <ModalPopupConfirmationComponent
        buttonText='Create'
        statusProcess='submit'
        opened={opened}
        open={open}
        close={close}
        onSubmit={handleConfirmSubmit}
      />
    </Container>
  );
};

export default AddRangeTemplatePage;
